import moment from "moment";
import {
  decimalPlaces,
  fastedMealIndicatorReport,
  getRoundedValue,
  roundMealValue,
} from "../../../../helpers/mealsHelper";
import { DateFormat } from "../../../../modules/constants";

export const headers24hRecall = {
  date: "Date",
  time: "Time",
  meal: "Meal",
  recall: "24hr Recall",
  hceMceEvent: "MCE/HCE Mitigation",
  calories: "Calories (kcal)",
  totalFat: "Total Fat (g)",
  longChainFat: "Long Chain Fat (g)",
  medChainFat: "Medium Chain Fat (g)",
  protein: "Protein (g)",
  carbohydrates: "Carbohydrates (g)",
  fiber: "Fiber (g)",
  linoleic: "Linoleic Acid (g)",
  linolenic: "Linolenic Acid (g)",
  ala: "ALA (g)",
  dha: "DHA (g)",
  vitA: "Vitamin A (IU)",
  vitD: "Vitamin D (IU)",
  vitE: "Vitamin E (mg)",
  vitEiU: "Vitamin E (IU)",
  vitK: "Vitamin K (mcg)",
  iron: "Iron (mg)",
  zinc: "Zinc (mg)",
  calcium: "Calcium (mg)",
  potassium: "Potassium (mg)",
  sodium: "Sodium (mg)",
  totalSugar: "Sugars - total (g)",
  addedSugars: "Sugars - added (g)",
  choline: "Choline - total (mg)",
  copper: "Copper (mg)",
  folic: "Folic Acid (mcg)",
  magnesium: "Magnesium (mg)",
  manganese: "Manganese (mg)",
  niacin: "Niacin (mg)",
  phosphorus: "Phosphorus (mg)",
  riboflavin: "Riboflavin (mg)",
  selenium: "Selenium (mcg)",
  thiamin: "Thiamin (mg)",
  vitB12: "Vitamin B-12 (mcg)",
  vitB6: "Vitamin B-6 (mg)",
  vitC: "Vitamin C - total ascorbic acid (mg)",
  lCarnitine: "L-Carnitine (mg)",
};

const getMealDescription = (meal) => {
  const itemTitles = meal.items.map((item, index) => {
    return index < 2 ? item.title : null;
  });

  let description = itemTitles.filter((title) => title !== null).join(" - ");

  if (meal.items.length > 2) {
    description += "...";
  }

  return description.replace(/,/g, "-");
};

const aggregateByDay = (data) => {
  const dataByDay = data
    .filter((meal) => meal.date === data[data.length - 1].date)
    .reduce(aggregatorByDay, {});

  return dataByDay;
};

export function getDataTo24HRecallReport(rawData) {
  const finalData = [];
  rawData.entries.total.forEach((meal) => {
    const mealParsed = JSON.parse(meal.mealJson);

    const mealDate = moment(meal.mealDate).format(DateFormat.BASE_FORMAT);

    if (
      finalData.length > 0 &&
      finalData[finalData.length - 1].date !== mealDate
    ) {
      finalData.push(aggregateByDay(finalData));
    }

    const newMeal = {
      date: mealDate,
      time: moment(meal.mealDate).format("hh:mm A"),
      meal: getMealDescription(mealParsed),
      recall: meal.recall ? "Y" : "",
      hceMceEvent: meal.hceMceEvent ? "Y" : "",
      ...getRoundedValuesWithMatchedKeys(mealParsed.aboutMeal),
    };

    if (!newMeal.meal) {
      newMeal.meal = fastedMealIndicatorReport;
    }

    //removing previous row (empty fasted) as we have more data for the day
    if (
      finalData[finalData.length - 1] &&
      finalData[finalData.length - 1].meal === fastedMealIndicatorReport
    ) {
      finalData.splice(-1);
    }

    finalData.push(newMeal);
  });

  finalData.push(aggregateByDay(finalData));

  // removing fasting day items where not needed
  const cleanData = [];
  finalData.forEach((item, index) => {
    if (
      !(
        finalData[index - 1] &&
        finalData[index - 1].date === item.date &&
        finalData[index - 1].meal !== fastedMealIndicatorReport &&
        item.meal === fastedMealIndicatorReport
      )
    ) {
      cleanData.push(item);
    }
  });

  return cleanData;
}

const matchParams = {
  calories: "Calories",
  totalFat: "Fats",
  longChainFat: "LongChainFats",
  medChainFat: "MediumChainFats",
  protein: "Protein",
  carbohydrates: "Carbs",
  fiber: "Fiber",
  linoleic: "LinoleicAcid",
  linolenic: "LinolenicAcid",
  ala: "ALA",
  dha: "DHA",
  vitA: "VitaminA",
  vitD: "VitaminD",
  vitE: "VitaminE",
  vitEiU: "VitaminE_UI",
  vitK: "VitaminK",
  iron: "Iron",
  zinc: "Zinc",
  calcium: "Calcium",
  potassium: "Potassium",
  sodium: "Sodium",
  totalSugar: "SugarsTotal",
  addedSugars: "SugarsAdded",
  choline: "CholineTotal",
  copper: "Copper",
  folic: "FolicAcid",
  magnesium: "Magnesium",
  manganese: "Manganese",
  niacin: "Niacin",
  phosphorus: "Phosphorus",
  riboflavin: "Riboflavin",
  selenium: "Selenium",
  thiamin: "Thiamin",
  vitB12: "VitaminB12",
  vitB6: "VitaminB6",
  vitC: "VitaminC_TotalAscorbicAcid",
  lCarnitine: "L_Carnitine",
};

const getRoundedValuesWithMatchedKeys = (aboutMeal) => {
  const newObj = {};
  Object.keys(matchParams).forEach((key) => {
    newObj[key] = getRoundedValue(aboutMeal[matchParams[key]]?.value);
  });
  return newObj;
};

const getObjectWithMatchedKeys = (accumulator, item) => {
  const newObj = {};
  Object.keys(matchParams).forEach((key) => {
    newObj[key] = `${Number(
      roundMealValue(Number(accumulator[key] ?? 0) + Number(item[key] ?? 0))
    ).toFixed(decimalPlaces)}`;
  });
  return newObj;
};

const aggregatorByDay = (accumulator, item) => {
  return {
    date: `${item.date} Total`,
    time: "",
    meal: "",
    recall: "",
    hceMceEvent: "",
    ...getObjectWithMatchedKeys(accumulator, item),
  };
};
